import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

export default function AuthPage() {
  const { login, register, error } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    if (mode === "login") await login(email, password);
    else await register(email, password, name);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen relative flex items-stretch">
      {/* Left visual pane */}
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1556139930-c23fa4a4f934?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#0F0E0C]/70" />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#CFA66A] flex items-center justify-center">
              <span className="font-mono text-[#CFA66A] text-xs">FJ</span>
            </div>
            <span className="font-head text-lg text-[#EBE5D9]">FOREX JOURNAL</span>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#CFA66A] mb-4">
              &mdash; The trader&apos;s ledger
            </p>
            <h1 className="font-head text-5xl xl:text-6xl font-bold text-[#EBE5D9] leading-[1.05] max-w-lg">
              Write the trade before the market writes you.
            </h1>
            <p className="mt-6 text-[#A39A8E] max-w-md">
              A structured, educational journal that turns every green &amp; red day into a lesson worth revisiting.
            </p>
          </div>
          <div className="font-mono text-[10px] tracking-widest text-[#A39A8E] uppercase">
            EST. 2026 &middot; Discipline compounds
          </div>
        </div>
      </div>

      {/* Right form pane */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-md slide-up">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-8 h-8 border border-[#CFA66A] flex items-center justify-center">
              <span className="font-mono text-[#CFA66A] text-xs">FJ</span>
            </div>
            <span className="font-head text-lg text-[#EBE5D9]">FOREX JOURNAL</span>
          </div>

          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#A39A8E] mb-3">
            {mode === "login" ? "// Sign in" : "// Create account"}
          </p>
          <h2 className="font-head text-4xl font-bold text-[#EBE5D9] mb-2">
            {mode === "login" ? "Welcome back." : "Start your journal."}
          </h2>
          <p className="text-[#A39A8E] mb-8">
            {mode === "login"
              ? "Pick up where you left off. Your discipline is waiting."
              : "Every professional trader keeps a journal. This is yours."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">Name</label>
                <input
                  data-testid={TID.authName}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] placeholder:text-[#A39A8E]/60 px-4 py-3 focus:outline-none focus:border-[#CFA66A] transition-colors rounded-sm"
                  placeholder="Alex Morgan"
                />
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">Email</label>
              <input
                data-testid={TID.authEmail}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] placeholder:text-[#A39A8E]/60 px-4 py-3 focus:outline-none focus:border-[#CFA66A] transition-colors rounded-sm"
                placeholder="you@trading.com"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">Password</label>
              <input
                data-testid={TID.authPassword}
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] placeholder:text-[#A39A8E]/60 px-4 py-3 focus:outline-none focus:border-[#CFA66A] transition-colors rounded-sm"
                placeholder="At least 6 characters"
              />
            </div>

            {error && (
              <p data-testid={TID.authError} className="text-[#B56258] text-sm font-mono">
                {error}
              </p>
            )}

            <button
              data-testid={mode === "login" ? TID.loginSubmit : TID.registerSubmit}
              type="submit"
              disabled={submitting}
              className="w-full bg-[#CFA66A] text-[#0F0E0C] font-medium py-3 rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50"
            >
              {submitting ? "..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <button
            data-testid={TID.toggleAuthMode}
            type="button"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-6 text-sm text-[#A39A8E] hover:text-[#CFA66A] transition-colors"
          >
            {mode === "login"
              ? "New here? Create an account →"
              : "Already have an account? Sign in →"}
          </button>
        </div>
      </div>
    </div>
  );
}
